// src/components/PursuitAccess/ExportManagerAssessmentsButton.jsx
import React from "react";
import { Button } from "grommet";

const fmtDate = (iso) =>
    iso
        ? new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
        : "";

const getUniqueUsers = (entries = []) => {
    const byKey = new Map();
    for (const e of entries) {
        const key = e.user_id ?? e.user_email ?? `${e.user_name}|${e.user_email}`;
        const existing = byKey.get(key);
        const completed = e.is_complete === 1;
        if (!existing) {
            byKey.set(key, {
                name: e.user_name || e.user_email || "",
                email: e.user_email || "",
                country: e.user_country || "",
                completed,
            });
        } else {
            existing.completed = existing.completed || completed;
        }
    }
    return Array.from(byKey.values());
};

const toSummaryRows = (groups = []) =>
    groups.map((g) => {
        const meta = g.assessment_meta || {};
        const entries = Array.isArray(g.entries) ? g.entries : [];
        const first = entries[0] || {};
        const uniqueUsers = getUniqueUsers(entries);
        const completedUsers = uniqueUsers.filter((u) => u.completed);
        const notCompletedUsers = uniqueUsers.filter((u) => !u.completed);
        const topics = Array.from(
            new Set(
                entries
                    .flatMap((e) => [e.topic1_name, e.topic2_name, e.topic3_name, e.topic4_name, e.topic5_name])
                    .filter(Boolean)
            )
        );
        return {
            Assessment: meta.assessment_name || "N/A",
            "Created Date": fmtDate(meta.creation_date),
            "Created Time": meta.creation_time || "",
            "Manager Name": first.manager_name || "",
            "Manager Email": first.manager_email || "",
            "Manager Country": first.manager_country || "",
            "Users Selected (Count)": uniqueUsers.length,
            "Completed (Count)": completedUsers.length,
            "Not Completed (Count)": notCompletedUsers.length,
            "Users Selected (Names)": uniqueUsers.map((u) => u.name).join("; "),
            "Users Selected (Emails)": uniqueUsers.map((u) => u.email).join("; "),
            "Completed Users (Emails)": completedUsers.map((u) => u.email).join("; "),
            "Not Completed Users (Emails)": notCompletedUsers.map((u) => u.email).join("; "),
            Topics: topics.join("; "),
        };
    });

const toUsersRows = (groups = []) =>
    groups.flatMap((g) => {
        const meta = g.assessment_meta || {};
        const entries = Array.isArray(g.entries) ? g.entries : [];
        const users = getUniqueUsers(entries);
        return users.map((u) => ({
            Assessment: meta.assessment_name || "N/A",
            "User Name": u.name,
            "User Email": u.email,
            "User Country": u.country,
            Completed: u.completed ? "Yes" : "No",
        }));
    });

const toEntryRows = (groups = []) =>
    groups.flatMap((g) => {
        const meta = g.assessment_meta || {};
        const entries = Array.isArray(g.entries) ? g.entries : [];
        return entries.map((e) => ({
            Assessment: meta.assessment_name || "N/A",
            "User Name": e.user_name || e.user_email || "",
            "User Email": e.user_email || "",
            "User Country": e.user_country || "",
            "Start Date": fmtDate(e.start_date),
            "Start Time": e.start_time || "",
            "End Date": fmtDate(e.end_date),
            "End Time": e.end_time || "",
            Questions: e.query_count ?? "",
            Correct: e.correct_count ?? "",
            Incorrect: e.incorrect_count ?? "",
            "Topic 1": e.topic1_name || "",
            "Topic 2": e.topic2_name || "",
            "Topic 3": e.topic3_name || "",
            "Topic 4": e.topic4_name || "",
            "Topic 5": e.topic5_name || "",
            Completed: e.is_complete === 1 ? "Yes" : "No",
        }));
    });

const rowsToCSV = (rows) => {
    const headers = Object.keys(rows[0] || {});
    const esc = (v) => {
        const s = (v ?? "").toString();
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(esc).join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))];
    return lines.join("\n");
};

const downloadBlob = (filename, mime, content) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

async function writeXlsx(sheets) {
    const { default: ExcelJS } = await import("exceljs");
    const wb = new ExcelJS.Workbook();
    for (const { name, rows } of sheets) {
        const ws = wb.addWorksheet(name);
        if (rows.length) {
            ws.columns = Object.keys(rows[0]).map((key) => ({ header: key, key }));
            rows.forEach((r) => ws.addRow(r));
        }
    }
    const buffer = await wb.xlsx.writeBuffer();
    return new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}

const ExportManagerAssessmentsButton = ({ groups = [], onDone }) => {
    const handleExport = async () => {
        const summary = toSummaryRows(groups);
        const users = toUsersRows(groups);
        const entries = toEntryRows(groups);

        const stamp = new Date().toISOString().slice(0, 10);
        const baseName = `HPE Digital Skill Advisor - Manager created assessments - ${stamp}`;

        try {
            const sheets = [
                { name: "Summary", rows: summary.length ? summary : [{ Info: "No data" }] },
                { name: "Users", rows: users.length ? users : [{ Info: "No users" }] },
                ...(entries.length ? [{ name: "Entries", rows: entries }] : []),
            ];
            const blob = await writeXlsx(sheets);
            downloadBlob(`${baseName}.xlsx`, null, blob);
            onDone?.(
                `Exported ${summary.length} assessment(s), ${users.length} unique user(s)${entries.length ? ` and ${entries.length} entry rows` : ""}.`
            );
        } catch {
            const csv = summary.length ? rowsToCSV(summary) : "No data";
            downloadBlob(`${baseName}.csv`, "text/csv;charset=utf-8", csv);
            onDone?.("Export complete (CSV fallback).");
        }
    };

    return (
        <Button
            label="Export"
            onClick={handleExport}
            secondary
            disabled={!groups.length}
        />
    );
};

export default ExportManagerAssessmentsButton;
